import { Fragment, useCallback, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError } from "@pontimed/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { DataNoticesPanel } from "@/components/admin/scraping/data-notices-panel";
import { JobLogViewer, type JobLogEntry } from "@/components/admin/scraping/job-log-viewer";
import { JobPreviewModal } from "@/components/admin/scraping/job-preview-modal";
import { JobProgress } from "@/components/admin/scraping/job-progress";
import { isScrapingRunsLocally } from "@/components/admin/scraping/scraping-runner";
import {
  useScrapingEvents,
  type ScrapingEvent,
} from "@/components/admin/scraping/use-scraping-events";
import { useAdminFetch } from "@/hooks/use-admin-fetch";
import { useAdminContext } from "@/lib/admin-context";
import { showToast } from "@/lib/ui/toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Admin → Scraping (operational view).
 *
 * Port of src/app/admin/scraping/{page,scraping-client}.tsx (legacy ~1923
 * LOC) per D21 NO REINVENTION — with PRAGMATIC SCOPE for V1.0:
 *
 *   - This commit ports the OPERATIONAL surface: country tabs + stats +
 *     sources list with Run button + jobs list with cancel/force-cancel.
 *   - DEFERRED V1.1: live progress bars + per-job log expansion + the
 *     facility/provider/business preview drill-down tabs (legacy
 *     ~600 LOC of detail rendering). Admin can already see job status,
 *     start new jobs, and abort stuck ones — that covers the V1.0
 *     cutover need. Live progress + previews land V1.1 alongside the
 *     workers runner port.
 *
 * Optimizations declared:
 *   - raw fetch + useAdminFetch hook → adminFetch (Architecture Discipline Rule 1)
 *   - useTranslations next-intl → Lingui Trans/t macros (D4)
 *   - useState/useEffect for fetch → useQuery + useMutation
 *   - showToast → imperative toast helper (lib/ui/toast.ts)
 *   - admin-db-context (X-Admin-DB header for local/prod toggle) → DEFERRED
 *     V1.1 (apps/api admin-scraping.ts uses default prisma; toggle is a
 *     legacy-only feature that lands when the DB toggle UI is ported).
 *
 * Endpoints consumed (apps/api admin-scraping — all live):
 *   GET    /api/admin/scraping/environment       — dev/local fingerprint
 *   GET    /api/admin/scraping/sources           — country catalog + sources
 *   GET    /api/admin/scraping/jobs              — paginated jobs list
 *   POST   /api/admin/scraping/jobs              — enqueue new job
 *   PATCH  /api/admin/scraping/jobs              — cancel (with force flag)
 *   GET    /api/admin/scraping/stats?country=XX  — totals (all or per country)
 */

interface ScrapingSource {
  key: string;
  name: string;
  type: string;
  enabled?: boolean;
  schedule?: string;
}

interface CountryData {
  code: string;
  name: Record<string, string>;
  flagEmoji: string;
  primaryLanguage: string;
  totalFacilities: number;
  totalProviders: number;
  scrapingSources: { sources?: ScrapingSource[] } | null;
}

interface ScrapingJob {
  id: string;
  source: string;
  country: string;
  region: string | null;
  status: string;
  recordsFound: number;
  recordsNew: number;
  recordsUpdated: number;
  recordsMerged?: number;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  log?: JobLogEntry[] | null;
}

interface ScrapingStats {
  totalJobs: number;
  running: number;
  recordsFound: number;
  recordsNew: number;
}

const statusConfig: Record<string, { color: string }> = {
  running: { color: "border-blue-300 bg-blue-50 text-blue-700" },
  pending: { color: "border-amber-300 bg-amber-50 text-amber-700" },
  completed: { color: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  failed: { color: "border-red-300 bg-red-50 text-red-700" },
  cancelled: { color: "border-zinc-300 bg-zinc-50 text-zinc-600" },
};

const sourceTypeColor: Record<string, string> = {
  facilities: "border-sky-300 bg-sky-50 text-sky-700",
  providers: "border-emerald-300 bg-emerald-50 text-emerald-700",
  businesses: "border-purple-300 bg-purple-50 text-purple-700",
  enrichment: "border-amber-300 bg-amber-50 text-amber-700",
  providers_and_facilities: "border-pink-300 bg-pink-50 text-pink-700",
  profile_completion: "border-purple-300 bg-purple-50 text-purple-700",
};

const hasLive = (status: string) => status === "running" || status === "pending";

/**
 * Per-source short description shown under the source name. Mirrors the
 * `sourceDescriptions` map from the legacy admin (src/app/admin/scraping/
 * scraping-client.tsx) — keys are the canonical source keys produced by
 * apps/api `/sources`. Wrapped via `t` so the strings extract into the
 * Lingui catalog and translate alongside the rest of the page.
 */
function useSourceLabels() {
  const { t } = useLingui();

  const statusLabel = (status: string): string => {
    switch (status) {
      case "running":
        return t`Rulează`;
      case "pending":
        return t`În așteptare`;
      case "completed":
        return t`Completat`;
      case "failed":
        return t`Eșuat`;
      case "cancelled":
        return t`Anulat`;
      default:
        return status;
    }
  };

  const typeLabel = (type: string): string => {
    switch (type) {
      case "facilities":
        return t`Unități`;
      case "providers":
        return t`Provideri`;
      case "businesses":
        return t`Business-uri`;
      case "enrichment":
        return t`Enrichment`;
      case "providers_and_facilities":
        return t`Provideri + Unități`;
      case "profile_completion":
        return t`Completare profil`;
      default:
        return type;
    }
  };

  const sourceDescription = (key: string): string => {
    switch (key) {
      case "overture":
        return t`Overture Maps Foundation — medical places from OpenStreetMap + Meta (free, global)`;
      case "cmr":
        return t`Romanian College of Physicians — profile completion: name, county, specialty. Blocked for now: the register requires reCAPTCHA`;
      case "ms_farmacii":
        return t`Ministry of Health — register of authorised pharmacies (Excel)`;
      case "nhis_facilities":
        return t`NHIS Bulgaria — 21,000+ medical facilities with specialties, doctors and addresses (opendata.his.bg)`;
      case "nhis_drugstores":
        return t`NHIS Bulgaria — drugstore register (non-prescription medicines only), ~350 shops with phone, email, website`;
      case "bls":
        return t`Bulgarian Medical Association — national register of ~35,000 doctors: name, УИН, specialties, city of the work address. No workplace, phone or photo`;
      case "bzs":
        return t`Bulgarian Dental Association — register of ~10,300 practising dentists (XLSX + public search): name, ЛПК, specialties, practice city`;
      case "nhis_pharmacies":
        return t`NHIS Bulgaria — pharmacy register: ~3,100 licensed pharmacies with permit holder, EIK, address and licensed activities (no phones)`;
      case "superdoc":
        return t`SuperDoc.bg — ~6,750 doctors + ~500 clinics: name, title, specialties, workplaces, address, coordinates, phone. No photos, bios, reviews or prices`;
      case "reginamaria":
        return t`Regina Maria — ~3,700 doctors + ~170 clinics and hospitals: name, grade, academic title, specialty, workplaces, address, coordinates, phone. No photos, bios, reviews or prices`;
      case "medlife":
        return t`MedLife — ~3,700 doctors, psychologists and therapists + ~110 clinics and hospitals: name, grade, specialty, workplaces, service names, address, coordinates, phone. No photos, bios or prices`;
      case "medicover":
        return t`Medicover (incl. Laurus, Phoenix) — ~1,500 doctors + ~57 clinics: name, grade, specialty, workplaces, service names, address, coordinates, phone. No photos, bios or prices`;
      case "cas_ro":
        return t`Public-insurance (CAS) contracts from the CNAS national lists (data.gov.ro) and CAS Maramureș: flags CAS-contracted facilities, pharmacies and labs, creates Bucharest doctors' practices — run after the other RO sources`;
      case "clickmed":
        return t`Clickmed.ro — ~6,000 doctors + ~700 clinics: name, grade, specialty, workplace, address, coordinates, service names. No photos, reviews or prices. 10 s per page: first full run ~21 h`;
      case "docbook":
        return t`Docbook.ro — ~9,000 doctors + 1,200 clinics: name, specialty, grade, workplace, address, coordinates. No photos, bios or prices`;
      case "aopsnaj_medici":
        return t`AOPSNAJ — ~300 family doctors with phone, email and full address (XLS, geocoded)`;
      case "aopsnaj_farmacii":
        return t`AOPSNAJ — ~1,500 pharmacies with phone, email and full address (XLS, geocoded)`;
      case "google_places":
        return t`Google Places API — enrichment: phone, opening hours (paid, ~$0.035/request)`;
      default:
        return "";
    }
  };

  const formatJobDate = (iso: string | null | undefined): string => {
    if (!iso) return "";
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return { statusLabel, typeLabel, sourceDescription, formatJobDate };
}

interface ScrapingSearch {
  country?: string;
}

export const Route = createFileRoute("/_authenticated/scraping")({
  validateSearch: (search: Record<string, unknown>): ScrapingSearch => ({
    country: typeof search.country === "string" ? search.country : undefined,
  }),
  component: ScrapingPage,
});

function ScrapingPage() {
  const { t } = useLingui();
  const { statusLabel, typeLabel, sourceDescription, formatJobDate } = useSourceLabels();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { adminFetch } = useAdminFetch();

  const { dbTarget, localDb, scrapingRunner } = useAdminContext();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(search.country ?? null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [previewJob, setPreviewJob] = useState<{
    jobId: string;
    source: string;
    country: string;
  } | null>(null);

  /**
   * Live overlay map. SSE events for the currently expanded running job land
   * here so progress bars + log tail update instantly without waiting for the
   * 2s table poll. Keys = jobId. Each entry merges into the polled row at
   * render time. Cleared once the job hits a terminal state (the table poll
   * subsequently delivers the canonical final row).
   */
  const [liveOverlay, setLiveOverlay] = useState<
    Record<
      string,
      {
        recordsFound?: number;
        recordsNew?: number;
        recordsUpdated?: number;
        recordsMerged?: number;
        status?: string;
        liveLog?: JobLogEntry[];
      }
    >
  >({});

  const handleScrapingEvent = useCallback(
    (event: ScrapingEvent) => {
      if (event.type === "error") {
        // Subscribe failure is non-fatal — the polling fallback covers it.
        return;
      }
      if (!("jobId" in event)) return;
      const jobId = event.jobId;
      setLiveOverlay((prev) => {
        const cur = prev[jobId] ?? { liveLog: [] };
        switch (event.type) {
          case "snapshot":
            return {
              ...prev,
              [jobId]: {
                ...cur,
                recordsFound: event.recordsFound,
                recordsNew: event.recordsNew,
                recordsUpdated: event.recordsUpdated,
                recordsMerged: event.recordsMerged,
                status: event.status,
              },
            };
          case "started":
            return { ...prev, [jobId]: { ...cur, status: "running" } };
          case "progress":
            return {
              ...prev,
              [jobId]: {
                ...cur,
                recordsFound: event.recordsFound,
                recordsNew: event.recordsNew,
                recordsUpdated: event.recordsUpdated,
                recordsMerged: event.recordsMerged,
                status: "running",
              },
            };
          case "log": {
            // Append to the in-memory tail (capped at 200 entries to keep the
            // expanded panel snappy on long runs — the persisted log on the
            // server still keeps the full tail).
            const next = [
              ...(cur.liveLog ?? []),
              {
                ts: event.ts,
                action: event.action,
                entity: event.entity,
                name: event.name,
                city: event.city,
                detail: event.detail,
              },
            ].slice(-200);
            return { ...prev, [jobId]: { ...cur, liveLog: next } };
          }
          case "complete":
            // Drop the overlay so the next poll's canonical row wins. Toast
            // gives operators an explicit "done" signal even if the table is
            // off-screen.
            showToast(`Job complete: ${event.recordsNew} new, ${event.recordsUpdated} updated`);
            return { ...prev, [jobId]: { ...cur, status: "completed" } };
          case "cancelled":
            return { ...prev, [jobId]: { ...cur, status: "cancelled" } };
          case "failed":
            return { ...prev, [jobId]: { ...cur, status: "failed" } };
          default:
            return prev;
        }
      });
      // On terminal events let the table poll refresh persisted state.
      if (event.type === "complete" || event.type === "cancelled" || event.type === "failed") {
        void queryClient.invalidateQueries({ queryKey: ["admin", "scraping"] });
      }
    },
    [queryClient],
  );

  // Subscribe ONLY when a job row is expanded AND it's actively running. The
  // polling fallback keeps non-expanded rows fresh enough for the high-level
  // pill state. (One SSE per page = bounded socket count even if a country
  // has 30+ historical jobs.)
  useScrapingEvents(expandedJobId, handleScrapingEvent);

  const setCountry = (code: string | null) => {
    setSelectedCountry(code);
    void navigate({
      search: () => ({ country: code ?? undefined }),
      replace: true,
    });
  };

  const sourcesQuery = useQuery({
    queryKey: ["admin", "scraping", "sources", dbTarget],
    queryFn: async (): Promise<CountryData[]> => {
      try {
        const data = await adminFetch.get<CountryData[]>("/api/admin/scraping/sources");
        return Array.isArray(data) ? data : [];
      } catch (error) {
        if (error instanceof ApiError) return [];
        throw error;
      }
    },
    staleTime: 60_000,
  });

  const jobsQuery = useQuery({
    queryKey: ["admin", "scraping", "jobs", selectedCountry, dbTarget],
    queryFn: async (): Promise<ScrapingJob[]> => {
      try {
        const params = new URLSearchParams({ limit: "100" });
        if (selectedCountry) params.set("country", selectedCountry);
        const data = await adminFetch.get<ScrapingJob[]>(
          `/api/admin/scraping/jobs?${params.toString()}`,
        );
        return Array.isArray(data) ? data : [];
      } catch (error) {
        if (error instanceof ApiError) return [];
        throw error;
      }
    },
    // Fast (2s) polling while any job is live for live progress + log tail;
    // slow (15s) once everything settled to keep stats fresh without burn.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!Array.isArray(data)) return 5_000;
      const hasLive = data.some((j) => j.status === "running" || j.status === "pending");
      return hasLive ? 2_000 : 15_000;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  const statsQuery = useQuery({
    queryKey: ["admin", "scraping", "stats", selectedCountry, dbTarget],
    queryFn: async (): Promise<ScrapingStats> => {
      try {
        const params = new URLSearchParams();
        if (selectedCountry) params.set("country", selectedCountry);
        const data = await adminFetch.get<Partial<ScrapingStats>>(
          `/api/admin/scraping/stats?${params.toString()}`,
        );
        return {
          totalJobs: data.totalJobs ?? 0,
          running: data.running ?? 0,
          recordsFound: data.recordsFound ?? 0,
          recordsNew: data.recordsNew ?? 0,
        };
      } catch (error) {
        if (error instanceof ApiError)
          return { totalJobs: 0, running: 0, recordsFound: 0, recordsNew: 0 };
        throw error;
      }
    },
    staleTime: 30_000,
  });

  const countries = sourcesQuery.data ?? [];
  const jobs = jobsQuery.data ?? [];
  const stats = statsQuery.data ?? { totalJobs: 0, running: 0, recordsFound: 0, recordsNew: 0 };

  const activeCountry = countries.find((c) => c.code === selectedCountry) ?? null;
  const activeSources = useMemo(() => {
    if (!activeCountry) return [];
    const all = activeCountry.scrapingSources?.sources ?? [];
    return all.filter((s) => s.enabled !== false);
  }, [activeCountry]);

  // Test mode toggle — when ON, the next "Run import" enqueues a job with
  // test-mode limits (~50 records, idempotent re-runs) regardless of which
  // DB target is selected. Defaults ON when targeting prod so an accidental
  // click can't pull the full registry into production. Persisted to the
  // session via sessionStorage so a refresh keeps the safety net.
  // The data is production when the Prod switch is on — or when this API's
  // own database is remote: on the deployed admin "Local" IS production, and
  // a default-off Test mode there made one click a full production import.
  const productionData =
    dbTarget === "prod" ||
    (localDb?.host
      ? !/^(localhost|127\.0\.0\.1|host\.docker\.internal)$/.test(localDb.host)
      : false);
  // The admin's explicit choice for this session; otherwise production data
  // defaults to Test mode ON. Derived, so it follows the environment once it loads.
  const [testModeChoice, setTestMode] = useState<boolean | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = window.sessionStorage.getItem("admin.scraping.testMode");
    return stored === "true" ? true : stored === "false" ? false : null;
  });
  const testMode = testModeChoice ?? productionData;
  const updateTestMode = (next: boolean) => {
    setTestMode(next);
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("admin.scraping.testMode", next ? "true" : "false");
    }
  };

  const enqueueMutation = useMutation({
    mutationFn: async ({ source, country }: { source: string; country: string }) => {
      await adminFetch.post("/api/admin/scraping/jobs", { source, country, testMode });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "scraping"] });
    },
    onError: (err) => {
      if (isScrapingRunsLocally(err)) {
        showToast(t`Scraping does not run on this server — start it from the Mac`, "error");
      } else if (err instanceof ApiError) {
        const reason =
          (typeof err.body === "object" && err.body && "error" in err.body
            ? (err.body as { error?: string }).error
            : null) ?? `${err.status}`;
        showToast(t`Failed to enqueue job: ${reason}`, "error");
      } else {
        showToast(t`Failed to enqueue job`, "error");
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ jobId, force }: { jobId: string; force?: boolean }) => {
      await adminFetch.patch("/api/admin/scraping/jobs", { jobId, force });
    },
    onSuccess: () => {
      showToast(t`Stopping the job…`);
      void queryClient.invalidateQueries({ queryKey: ["admin", "scraping"] });
    },
    onError: (err) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "scraping"] });
      // 409: the job ended on its own before the stop reached it.
      if (err instanceof ApiError && err.status === 409) {
        showToast(t`The job had already finished`, "info");
        return;
      }
      showToast(t`Could not stop the job`, "error");
    },
  });

  const meiliSyncMutation = useMutation({
    mutationFn: async (country: string) => {
      return await adminFetch.post<{
        providers?: number;
        businesses?: number;
        synced?: number;
        dbTarget?: string;
      }>("/api/admin/search-sync", { country });
    },
    onSuccess: (data, country) => {
      const synced = data.providers ?? data.synced ?? 0;
      const businesses = data.businesses ?? 0;
      showToast(
        t`Meilisearch resynced for ${country}: ${synced} providers, ${businesses} businesses (${data.dbTarget ?? "?"})`,
        "success",
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 501) {
        showToast(t`Meilisearch resync pending — apps/api W7 port required.`, "info");
        return;
      }
      const reason = err instanceof Error ? err.message : t`Network error`;
      showToast(t`Sync failed: ${reason}`, "error");
    },
  });

  const deleteScrapedMutation = useMutation({
    mutationFn: async (country: string) => {
      // apps/api DELETE handlers expect `{ allScraped: true, country }` in the
      // JSON body (legacy contract). Earlier port had them as query params,
      // which routes never read → 400 + silent { deleted: 0 } responses.
      const headers = {
        "X-Admin-Confirm-Delete": "true",
        "Content-Type": "application/json",
      };
      const deleteBulk = async (path: string): Promise<{ deleted: number; error?: string }> => {
        const res = await adminFetch.raw(path, {
          method: "DELETE",
          headers,
          body: JSON.stringify({ allScraped: true, country }),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          return { deleted: 0, error: `${res.status} ${text}`.trim() };
        }
        return res.json() as Promise<{ deleted: number }>;
      };
      const [fac, prov, biz] = await Promise.all([
        deleteBulk("/api/admin/facilities"),
        deleteBulk("/api/admin/providers"),
        deleteBulk("/api/admin/businesses"),
      ]);
      const jobsRes = await adminFetch.raw("/api/admin/scraping/jobs", {
        method: "DELETE",
        headers,
        body: JSON.stringify({ country }),
      });
      const jobs = jobsRes.ok
        ? ((await jobsRes.json()) as { deleted: number; error?: string })
        : { deleted: 0, error: `${jobsRes.status} ${await jobsRes.text().catch(() => "")}`.trim() };
      return { fac, prov, biz, jobs };
    },
    onSuccess: (data, country) => {
      const errs = [data.fac.error, data.prov.error, data.biz.error, data.jobs.error].filter(
        (e): e is string => !!e,
      );
      if (errs.length > 0) {
        showToast(
          t`Delete from ${country} partially failed: ${errs.join("; ").slice(0, 200)}`,
          "error",
        );
      } else {
        showToast(
          t`Deleted from ${country}: ${data.fac.deleted} facilities + ${data.prov.deleted} providers + ${data.biz.deleted} businesses + ${data.jobs.deleted} jobs`,
          "success",
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["admin", "scraping"] });
    },
    onError: (err) => {
      const reason = err instanceof Error ? err.message : t`Network error`;
      showToast(t`Delete failed: ${reason}`, "error");
    },
  });

  if (sourcesQuery.isLoading && countries.length === 0) {
    return (
      <div className="text-muted-foreground p-8 text-sm">
        <Trans>Loading scraping configuration…</Trans>
      </div>
    );
  }

  const countryName = (c: CountryData) => c.name?.en || c.name?.ro || c.code;

  const countryStats = (code: string) => {
    const cJobs = jobs.filter((j) => j.country === code);
    return {
      totalJobs: cJobs.length,
      running: cJobs.filter((j) => j.status === "running" || j.status === "pending").length,
    };
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-foreground text-2xl font-bold tracking-tight">
            <Trans>Scraping</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>
              Operational view: source catalog, job queue, run/cancel controls. Detail preview
              drill-downs ship V1.1.
            </Trans>
          </p>
        </div>
        {/* Test mode toggle — clamps the next "Run import" to a small batch
            (~50 records) regardless of which DB the worker writes to. Defaults
            ON when targeting prod so an accidental click on a real source
            can't pull the full 21k registry into production. Off by default
            on local where dev limits already apply. */}
        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors select-none ${
            testMode
              ? "border-warning/40 bg-warning-soft text-warning-strong"
              : "border-border bg-card text-muted-foreground hover:bg-muted/50"
          }`}
          title={t`When ON, the next Run import is capped at a small batch (50 records by default, set by SCRAPING_TEST_IMPORT_LIMIT on the worker) and skips existing rows.`}
        >
          <input
            type="checkbox"
            className="h-4 w-4 accent-current"
            checked={testMode}
            onChange={(e) => updateTestMode(e.target.checked)}
          />
          <span className="font-medium">
            {testMode ? <Trans>🧪 Test mode (small batch)</Trans> : <Trans>🧪 Test mode</Trans>}
          </span>
        </label>
      </header>

      {/* The deployed API has no scraping worker (decision 2026-10-28): runs
          go from the Mac, against this same database, and show up here. */}
      {!scrapingRunner && (
        <div
          role="status"
          className="border-warning/40 bg-warning-soft text-warning-strong space-y-1 rounded-lg border px-4 py-3 text-sm"
        >
          <p className="font-medium">
            <Trans>Scraping does not run on this server</Trans>
          </p>
          <p>
            <Trans>
              Production scraping runs from the Mac or the mini PC:{" "}
              <code className="font-mono text-xs">scripts/run-scrape-job.ts --prod</code>, or the
              local admin with the Prod switch on. Jobs, logs and Stop still work here.
            </Trans>
          </p>
        </div>
      )}

      {countries.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground text-sm">
              <Trans>
                No countries with scraping enabled. Activate "Scraping enabled" in country settings.
              </Trans>
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCountry(null)}
              className={`relative flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all ${
                selectedCountry === null
                  ? "border-primary bg-primary/5 text-primary shadow-sm"
                  : "border-border bg-card text-muted-foreground hover:border-primary/30 hover:bg-muted/50"
              }`}
            >
              <span className="text-lg">🌍</span>
              <Trans>All</Trans>
            </button>
            {countries.map((c) => {
              const cs = countryStats(c.code);
              const isSelected = selectedCountry === c.code;
              return (
                <button
                  key={c.code}
                  onClick={() => setCountry(c.code)}
                  className={`relative flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all ${
                    isSelected
                      ? "border-primary bg-primary/5 text-primary shadow-sm"
                      : "border-border bg-card text-muted-foreground hover:border-primary/30 hover:bg-muted/50"
                  }`}
                >
                  <span className="text-lg">{c.flagEmoji}</span>
                  <span>{countryName(c)}</span>
                  {cs.running > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[0.625rem] text-white">
                      {cs.running}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            {[
              { label: <Trans>Jobs total</Trans>, value: stats.totalJobs.toString(), icon: "📦" },
              { label: <Trans>Running</Trans>, value: stats.running.toString(), icon: "⏳" },
              {
                label: <Trans>Records found</Trans>,
                value: stats.recordsFound.toLocaleString(),
                icon: "🔍",
              },
              {
                label: <Trans>Records new</Trans>,
                value: stats.recordsNew.toLocaleString(),
                icon: "✨",
              },
            ].map((s, i) => (
              <Card key={i}>
                <CardContent className="flex items-center gap-3 p-4">
                  <span className="text-2xl">{s.icon}</span>
                  <div>
                    <div className="text-xl font-bold">{s.value}</div>
                    <div className="text-muted-foreground text-xs">{s.label}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {activeCountry && (
            <Card>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{activeCountry.flagEmoji}</span>
                  <div>
                    <h2 className="text-foreground text-lg font-semibold">
                      {countryName(activeCountry)}
                    </h2>
                    <p className="text-muted-foreground text-xs">
                      <Trans>
                        {activeCountry.totalFacilities.toLocaleString()} facilities ·{" "}
                        {activeCountry.totalProviders.toLocaleString()} providers · Lang:{" "}
                        {activeCountry.primaryLanguage.toUpperCase()}
                      </Trans>
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={meiliSyncMutation.isPending}
                    onClick={() => meiliSyncMutation.mutate(activeCountry.code)}
                  >
                    {meiliSyncMutation.isPending ? (
                      <Trans>Syncing…</Trans>
                    ) : (
                      <Trans>Resync Meilisearch</Trans>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:border-red-300 hover:text-red-700 dark:text-red-400 dark:hover:border-red-900 dark:hover:text-red-300"
                    disabled={deleteScrapedMutation.isPending}
                    onClick={() => {
                      const ok = window.confirm(
                        t`Delete ALL scraped facilities, providers, and businesses for ${activeCountry.code}? This cannot be undone.`,
                      );
                      if (ok) deleteScrapedMutation.mutate(activeCountry.code);
                    }}
                  >
                    {deleteScrapedMutation.isPending ? (
                      <Trans>Deleting…</Trans>
                    ) : (
                      <Trans>Delete imported data</Trans>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeCountry && activeSources.length > 0 && (
            <div>
              <h3 className="text-muted-foreground mb-3 text-sm font-semibold tracking-wide uppercase">
                <Trans>
                  Sources for {countryName(activeCountry)} ({activeSources.length})
                </Trans>
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {activeSources.map((src) => {
                  const hasActiveJob = jobs.some(
                    (j) =>
                      j.country === activeCountry.code &&
                      j.source === src.key &&
                      (j.status === "running" || j.status === "pending"),
                  );
                  const activeJob = jobs.find(
                    (j) =>
                      j.country === activeCountry.code &&
                      j.source === src.key &&
                      (j.status === "running" || j.status === "pending"),
                  );
                  const lastJob = jobs.find(
                    (j) => j.country === activeCountry.code && j.source === src.key,
                  );
                  // Merge live SSE overlay so the source card mirrors the row
                  // counters between 2s polls (Status pill, Găsite, Noi).
                  const overlay = lastJob ? liveOverlay[lastJob.id] : undefined;
                  const liveLast = lastJob
                    ? {
                        ...lastJob,
                        status: overlay?.status ?? lastJob.status,
                        recordsFound: overlay?.recordsFound ?? lastJob.recordsFound,
                        recordsNew: overlay?.recordsNew ?? lastJob.recordsNew,
                        recordsUpdated: overlay?.recordsUpdated ?? lastJob.recordsUpdated,
                      }
                    : null;
                  const typeColor = sourceTypeColor[src.type] || "";
                  const description = sourceDescription(src.key);
                  const lastWhen = liveLast?.completedAt ?? liveLast?.startedAt ?? null;
                  return (
                    <Card key={src.key}>
                      <CardContent className="space-y-3 p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="font-semibold">{src.name}</h4>
                              <Badge variant="outline" className={typeColor}>
                                {typeLabel(src.type)}
                              </Badge>
                            </div>
                            {description && (
                              <p className="text-muted-foreground mt-1.5 text-xs leading-relaxed">
                                {description}
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-start">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!liveLast || liveLast.recordsNew === 0}
                              title={
                                !liveLast || liveLast.recordsNew === 0
                                  ? t`Niciun import cu date noi de previzualizat`
                                  : t`Vezi datele importate`
                              }
                              onClick={() => {
                                if (!liveLast || liveLast.recordsNew === 0) return;
                                setPreviewJob({
                                  jobId: liveLast.id,
                                  source: liveLast.source,
                                  country: liveLast.country,
                                });
                              }}
                            >
                              <Trans>🔍 Vezi date</Trans>
                            </Button>
                            <Button
                              size="sm"
                              disabled={
                                !scrapingRunner || hasActiveJob || enqueueMutation.isPending
                              }
                              title={
                                scrapingRunner
                                  ? undefined
                                  : t`Runs from the Mac, not on this server`
                              }
                              onClick={() =>
                                enqueueMutation.mutate({
                                  source: src.key,
                                  country: activeCountry.code,
                                })
                              }
                            >
                              {hasActiveJob ? (
                                <Trans>Rulează…</Trans>
                              ) : (
                                <Trans>Rulează import</Trans>
                              )}
                            </Button>
                          </div>
                        </div>
                        {liveLast && (
                          <div className="border-border/50 space-y-1.5 border-t pt-3 text-xs">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge
                                variant="outline"
                                className={statusConfig[liveLast.status]?.color || ""}
                              >
                                {statusLabel(liveLast.status)}
                              </Badge>
                              {lastWhen && (
                                <span className="text-muted-foreground">
                                  {formatJobDate(lastWhen)}
                                </span>
                              )}
                              {hasActiveJob && activeJob && (
                                <div className="ml-auto flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-1.5 text-xs text-red-500 hover:text-red-700"
                                    onClick={() => cancelMutation.mutate({ jobId: activeJob.id })}
                                    disabled={cancelMutation.isPending}
                                  >
                                    <Trans>Stop</Trans>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-1.5 text-xs font-bold text-red-600 hover:text-red-800"
                                    onClick={() =>
                                      cancelMutation.mutate({
                                        jobId: activeJob.id,
                                        force: true,
                                      })
                                    }
                                    disabled={cancelMutation.isPending}
                                    title={t`Forțează oprirea — marchează jobul ca anulat în DB`}
                                  >
                                    <Trans>⚡ Force</Trans>
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              <span>
                                <Trans>Găsite</Trans>:{" "}
                                <strong className="tabular-nums">
                                  {liveLast.recordsFound.toLocaleString()}
                                </strong>
                              </span>
                              <span className="text-emerald-600 dark:text-emerald-400">
                                <Trans>Noi</Trans>:{" "}
                                <strong className="tabular-nums">
                                  {liveLast.recordsNew.toLocaleString()}
                                </strong>
                              </span>
                              <span>
                                <Trans>Update</Trans>:{" "}
                                <strong className="tabular-nums">
                                  {liveLast.recordsUpdated.toLocaleString()}
                                </strong>
                              </span>
                            </div>
                            {liveLast.errorMessage && (
                              <p className="text-destructive truncate">{liveLast.errorMessage}</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {activeCountry && (
            <DataNoticesPanel key={activeCountry.code} country={activeCountry.code} />
          )}

          <div>
            <h3 className="text-muted-foreground mb-3 text-sm font-semibold tracking-wide uppercase">
              <Trans>Recent jobs ({jobs.length})</Trans>
            </h3>
            <Card>
              <CardContent className="p-0">
                {jobs.length === 0 ? (
                  <div className="text-muted-foreground p-8 text-center text-sm">
                    <Trans>No jobs in queue.</Trans>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            <Trans>Source</Trans>
                          </TableHead>
                          <TableHead className="hidden sm:table-cell">
                            <Trans>Country</Trans>
                          </TableHead>
                          <TableHead>
                            <Trans>Status</Trans>
                          </TableHead>
                          <TableHead className="hidden text-right md:table-cell">
                            <Trans>Found</Trans>
                          </TableHead>
                          <TableHead className="hidden text-right md:table-cell">
                            <Trans>New</Trans>
                          </TableHead>
                          <TableHead className="hidden lg:table-cell">
                            <Trans>Started</Trans>
                          </TableHead>
                          <TableHead className="text-right">
                            <Trans>Actions</Trans>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {jobs.map((job) => {
                          // Merge SSE overlay (if any) into the polled row so
                          // counters tick up between 2s polls and the status
                          // pill flips immediately on terminal events.
                          const overlay = liveOverlay[job.id];
                          const liveJob: ScrapingJob = overlay
                            ? {
                                ...job,
                                status: overlay.status ?? job.status,
                                recordsFound: overlay.recordsFound ?? job.recordsFound,
                                recordsNew: overlay.recordsNew ?? job.recordsNew,
                                recordsUpdated: overlay.recordsUpdated ?? job.recordsUpdated,
                                recordsMerged: overlay.recordsMerged ?? job.recordsMerged,
                              }
                            : job;
                          const isActive =
                            liveJob.status === "running" || liveJob.status === "pending";
                          const polledLog: JobLogEntry[] = Array.isArray(job.log) ? job.log : [];
                          // While SSE is feeding entries, prefer the in-memory
                          // tail (matches what the runner just published);
                          // fall back to the persisted log between connects.
                          const logEntries: JobLogEntry[] =
                            overlay?.liveLog && overlay.liveLog.length > 0
                              ? overlay.liveLog
                              : polledLog;
                          const hasLog = logEntries.length > 0;
                          const isExpanded = expandedJobId === job.id;
                          const canPreview = liveJob.recordsNew > 0 && !isActive;
                          return (
                            <Fragment key={job.id}>
                              <TableRow>
                                <TableCell className="font-medium">
                                  {job.source}
                                  {job.region && (
                                    <span className="text-muted-foreground ml-1 text-xs">
                                      · {job.region}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="hidden text-xs sm:table-cell">
                                  {job.country}
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={statusConfig[liveJob.status]?.color || ""}
                                  >
                                    {statusLabel(liveJob.status)}
                                  </Badge>
                                  {liveJob.errorMessage && (
                                    <p className="text-destructive mt-1 max-w-xs truncate text-[0.625rem]">
                                      {liveJob.errorMessage}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="hidden text-right tabular-nums md:table-cell">
                                  {liveJob.recordsFound.toLocaleString()}
                                </TableCell>
                                <TableCell className="hidden text-right font-medium text-emerald-600 tabular-nums md:table-cell">
                                  {liveJob.recordsNew.toLocaleString()}
                                </TableCell>
                                <TableCell className="text-muted-foreground hidden text-xs lg:table-cell">
                                  {job.startedAt
                                    ? new Date(job.startedAt).toLocaleString()
                                    : new Date(job.createdAt).toLocaleString()}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex flex-wrap justify-end gap-1">
                                    {(hasLive(liveJob.status) || hasLog) && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2 text-xs"
                                        onClick={() => setExpandedJobId(isExpanded ? null : job.id)}
                                      >
                                        {isExpanded ? (
                                          <Trans>Hide</Trans>
                                        ) : (
                                          <Trans>Details ({logEntries.length})</Trans>
                                        )}
                                      </Button>
                                    )}
                                    {canPreview && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2 text-xs"
                                        onClick={() =>
                                          setPreviewJob({
                                            jobId: job.id,
                                            source: job.source,
                                            country: job.country,
                                          })
                                        }
                                        title={t`Vezi datele importate`}
                                      >
                                        <Trans>🔍 Vezi date</Trans>
                                      </Button>
                                    )}
                                    {isActive && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 px-2 text-xs text-red-500 hover:text-red-700"
                                          onClick={() => cancelMutation.mutate({ jobId: job.id })}
                                          disabled={cancelMutation.isPending}
                                        >
                                          <Trans>Stop</Trans>
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 px-2 text-xs font-bold text-red-600 hover:text-red-800"
                                          onClick={() =>
                                            cancelMutation.mutate({
                                              jobId: job.id,
                                              force: true,
                                            })
                                          }
                                          disabled={cancelMutation.isPending}
                                          title={t`Force-cancel — flips DB row to cancelled (for stuck jobs)`}
                                        >
                                          <Trans>⚡ Force</Trans>
                                        </Button>
                                      </>
                                    )}
                                    {!isActive && !canPreview && !hasLog && (
                                      <span className="text-muted-foreground text-xs">—</span>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                              {isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={7} className="bg-muted/20 p-3">
                                    <ExpandedJobPanel
                                      job={liveJob}
                                      polledLog={polledLog}
                                      liveLog={overlay?.liveLog}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <JobPreviewModal
        open={previewJob !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewJob(null);
        }}
        jobId={previewJob?.jobId ?? null}
        source={previewJob?.source ?? null}
        country={previewJob?.country ?? null}
      />
    </div>
  );
}

/**
 * Expanded job detail panel — owns the per-job full-log fetch so we don't
 * pull every log on first render of the table. Lazy: only mounts (and
 * therefore queries) when the row is open. Falls back to whatever the table
 * poll already has if the dedicated GET fails (e.g. job was deleted between
 * expand and fetch).
 */
function ExpandedJobPanel({
  job,
  polledLog,
  liveLog,
}: {
  job: ScrapingJob;
  polledLog: JobLogEntry[];
  liveLog: JobLogEntry[] | undefined;
}) {
  const { adminFetch } = useAdminFetch();
  const fullLogQuery = useQuery({
    queryKey: ["admin", "scraping", "job-log", job.id],
    queryFn: async () => {
      try {
        return await adminFetch.get<{ log: JobLogEntry[] }>(
          `/api/admin/scraping/jobs/${encodeURIComponent(job.id)}/log`,
        );
      } catch (err) {
        if (err instanceof ApiError) return { log: [] };
        throw err;
      }
    },
    // Live jobs: the SSE stream feeds incremental entries; don't refetch the
    // full log (would clobber liveLog tail). Settled jobs: cache 1 min so
    // collapse + re-expand is instant.
    enabled: !hasLive(job.status),
    staleTime: 60_000,
  });

  // Resolution order while live: liveLog (SSE tail) > polledLog (table row).
  // Resolution order when settled: full log fetch > polledLog (used while
  // the dedicated request is in flight).
  const isLive = hasLive(job.status);
  const entries: JobLogEntry[] = isLive
    ? liveLog && liveLog.length > 0
      ? liveLog
      : polledLog
    : (fullLogQuery.data?.log ?? polledLog);

  return (
    <div className="space-y-3">
      <JobProgress job={job} />
      <div>
        <div className="text-muted-foreground mb-1.5 text-[0.6875rem] font-semibold tracking-wide uppercase">
          <Trans>Job log</Trans>
        </div>
        <JobLogViewer entries={entries} isLive={isLive} />
      </div>
    </div>
  );
}
